/**
 * CaptchaModal - Reusable hCaptcha modal component
 * 
 * Usage:
 * <CaptchaModal
 *   isOpen={showCaptcha}
 *   onVerify={(token) => handleVerify(token)}
 *   onClose={() => setShowCaptcha(false)}
 * />
 */

import { useRef } from 'react';
import HCaptcha from '@hcaptcha/react-hcaptcha';
import './CaptchaModal.css';

const HCAPTCHA_SITE_KEY = import.meta.env.VITE_HCAPTCHA_SITE_KEY || '';

interface CaptchaModalProps {
    isOpen: boolean;
    onVerify: (token: string) => void;
    onClose: () => void;
    title?: string;
    description?: string;
}

export default function CaptchaModal({
    isOpen,
    onVerify,
    onClose,
    title = 'Verificare de securitate',
    description = 'Te rugăm să completezi verificarea pentru a continua.',
}: CaptchaModalProps) {
    const captchaRef = useRef<HCaptcha>(null);

    if (!isOpen) return null;

    const handleVerify = (token: string) => {
        onVerify(token);
    };

    const handleError = () => {
        captchaRef.current?.resetCaptcha();
    };

    const handleExpire = () => {
        captchaRef.current?.resetCaptcha();
    };

    return (
        <div className="captcha-modal-overlay" onClick={onClose}>
            <div className="captcha-modal" onClick={(e) => e.stopPropagation()}>
                <button className="captcha-close-btn" onClick={onClose}>
                    <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                        <path d="M18 6L6 18M6 6l12 12" />
                    </svg>
                </button>

                <div className="captcha-icon">🔒</div>
                <h3>{title}</h3>
                <p>{description}</p>

                <div className="captcha-widget">
                    {HCAPTCHA_SITE_KEY ? (
                        <HCaptcha
                            ref={captchaRef}
                            sitekey={HCAPTCHA_SITE_KEY}
                            onVerify={handleVerify}
                            onError={handleError}
                            onExpire={handleExpire}
                        />
                    ) : (
                        <p className="captcha-error">
                            hCaptcha nu este configurat. Verifică variabila VITE_HCAPTCHA_SITE_KEY.
                        </p>
                    )}
                </div>
            </div>
        </div>
    );
}
